// src/pages/professional/Procedures.jsx
import React, { useState } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { useApp } from 'contexts/AppContext';
import { proceduresService } from 'infrastructure/firebase';
import { Button, Modal, FormGroup, Input, Select, EmptyState, ProcedureCard } from 'shared/components/ui';
import { getAutonomousPlan, autonomousProcedureLimit } from 'shared/config/plans';

const CATEGORIES = ['Faciais', 'Corporais', 'Injetáveis', 'Capilar', 'Massagem', 'Outros'];
const EMOJIS     = ['💆','✨','💉','💋','🌊','⚡','🌿','🧴','🪷','💎'];

export default function ProfessionalProcedures() {
  const { userProfile } = useAuth();
  const { procedures, showToast } = useApp();

  // Autônomo = profissional sem estabelecimentoId vinculado
  const isAutonomous = !userProfile?.estabelecimentoId;
  // Plano do autônomo define o limite de procedimentos (null = ilimitado).
  const plan      = getAutonomousPlan(userProfile?.planoId);
  const procLimit = autonomousProcedureLimit(userProfile?.planoId);

  // Unifica leitura: professionals[] (padrão) OU professionalId (legado)
  const myProcedures = procedures.filter(p => {
    const ids = (p.professionals?.length > 0) ? p.professionals : (p.professionalId ? [p.professionalId] : []);
    return ids.includes(userProfile?.id);
  });
  const approved = myProcedures.filter(p => p.approved);
  const pending  = myProcedures.filter(p => !p.approved);

  // Só o autônomo tem teto; ilimitado (procLimit == null) nunca atinge limite.
  const atLimit = isAutonomous && procLimit != null && myProcedures.length >= procLimit;

  const [modal, setModal]       = useState(false);
  const [editProc, setEditProc] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [form, setForm]         = useState({
    name:'', category:'Faciais', duration:60, price:'', emoji:'💆', description:''
  });

  const openNew = () => { setEditProc(null); setForm({ name:'', category:'Faciais', duration:60, price:'', emoji:'💆', description:'' }); setModal(true); };
  const openEdit = (p) => { setEditProc(p); setForm({ name:p.name, category:p.category||'Outros', duration:p.duration||60, price:p.price||'', emoji:p.emoji||'💆', description:p.description||'' }); setModal(true); };

  const handleSave = async () => {
    if (!form.name || !form.price) return;
    if (!editProc && atLimit) {
      showToast('Limite de procedimentos atingido para o plano autônomo.', 'warning');
      return;
    }
    setLoading(true);
    try {
      const uid = userProfile?.id;
      const data = {
        ...form,
        price:    Number(String(form.price).replace(',', '.')),
        duration: Number(form.duration),
        // Padrão unificado: professionals[] (array). professionalId mantido como legado de leitura.
        professionals:    editProc?.professionals?.includes(uid)
          ? editProc.professionals
          : [...new Set([...(editProc?.professionals || []), uid])],
        professionalId:   uid,
        professionalName: userProfile?.name,
        estabelecimentoId: userProfile?.estabelecimentoId || null,
      };
      if (editProc) {
        await proceduresService.update(editProc.id, { ...data, approved: editProc.approved });
        showToast('Procedimento atualizado!');
      } else {
        // Autônomo (sem clínica) não tem admin para aprovar: publica direto (dentro
        // do limite do plano, já validado em `atLimit`). Profissional de clínica
        // continua precisando da aprovação do admin do estabelecimento.
        await proceduresService.add({ ...data, professionals: [uid], approved: isAutonomous });
        showToast(isAutonomous
          ? 'Procedimento publicado! Já está disponível para agendamento.'
          : 'Procedimento enviado para aprovação! O administrador irá analisar em breve.');
      }
      setModal(false);
    } catch (err) {
      showToast('Erro ao salvar procedimento.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (proc) => {
    if (!window.confirm(`Excluir "${proc.name}"?`)) return;
    try {
      await proceduresService.delete(proc.id);
      showToast('Procedimento removido.');
    } catch { showToast('Erro ao remover procedimento.', 'danger'); }
  };

  return (
    <div>
      {/* Limite autônomo */}
      {isAutonomous && (
        <div style={{ background: atLimit ? '#FEE2E2' : '#EFF6FF',
          border: `1.5px solid ${atLimit ? '#FCA5A5' : '#93C5FD'}`,
          borderRadius:'var(--radius)', padding:'12px 18px', marginBottom:20,
          display:'flex', alignItems:'center', gap:12,
          color: atLimit ? '#991B1B' : '#1E40AF' }}>
          <span style={{ fontSize:20 }}>{atLimit ? '🔒' : '💡'}</span>
          <div>
            <div style={{ fontWeight:600, marginBottom:2 }}>
              Plano {plan.nome}: {myProcedures.length}/{procLimit == null ? '∞' : procLimit} procedimentos
            </div>
            <div style={{ fontSize:13 }}>
              {procLimit == null
                ? 'Procedimentos ilimitados — publicados automaticamente, sem aprovação.'
                : atLimit
                ? 'Limite do plano atingido. Faça upgrade do plano para cadastrar mais.'
                : `Você pode cadastrar mais ${procLimit - myProcedures.length} procedimento(s). Publicação automática, sem aprovação.`}
            </div>
          </div>
        </div>
      )}

      {/* Pending alert */}
      {pending.length > 0 && (
        <div style={{ background:'#fffbeb', border:'1.5px solid #fbbf24', borderRadius:'var(--radius)', padding:'14px 18px', marginBottom:20, color:'#92400e', display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:20 }}>⏳</span>
          <div>
            <div style={{ fontWeight:600, marginBottom:2 }}>{pending.length} procedimento(s) aguardando aprovação</div>
            <div style={{ fontSize:13 }}>O administrador irá analisar em breve. Você será notificado.</div>
          </div>
        </div>
      )}

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h2 style={{ fontFamily:"'Instrument Serif',serif", fontSize:22 }}>Meus Procedimentos</h2>
          <p style={{ color:'var(--text-muted)', fontSize:13, marginTop:2 }}>
            {isAutonomous
              ? 'Gerencie os serviços que você oferece. Como autônomo, seus procedimentos são publicados automaticamente.'
              : 'Gerencie os serviços que você oferece. Novos procedimentos precisam de aprovação do admin.'}
          </p>
        </div>
        <Button variant="primary" onClick={openNew} disabled={atLimit}>
          + Cadastrar Procedimento
        </Button>
      </div>

      {/* Approved */}
      {approved.length > 0 && (
        <div style={{ marginBottom:28 }}>
          <div style={{ fontSize:12, fontWeight:600, textTransform:'uppercase', letterSpacing:1.5, color:'var(--text-muted)', marginBottom:14 }}>
            Aprovados ({approved.length})
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:16 }}>
            {approved.map(p => (
              <ProcedureCard key={p.id} procedure={p}
                actions={
                  <div style={{ display:'flex', gap:8 }}>
                    <Button variant="secondary" size="sm" style={{ flex:1 }} onClick={() => openEdit(p)}>✏️ Editar</Button>
                    <Button variant="danger" size="sm" onClick={() => handleDelete(p)}>🗑️</Button>
                  </div>
                }
              />
            ))}
          </div>
        </div>
      )}

      {/* Pending */}
      {pending.length > 0 && (
        <div>
          <div style={{ fontSize:12, fontWeight:600, textTransform:'uppercase', letterSpacing:1.5, color:'var(--text-muted)', marginBottom:14 }}>
            Aguardando aprovação ({pending.length})
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:16 }}>
            {pending.map(p => (
              <ProcedureCard key={p.id} procedure={p}
                actions={
                  <div style={{ display:'flex', gap:8 }}>
                    <Button variant="secondary" size="sm" style={{ flex:1 }} onClick={() => openEdit(p)}>✏️ Editar</Button>
                    <Button variant="danger" size="sm" onClick={() => handleDelete(p)}>🗑️</Button>
                  </div>
                }
              />
            ))}
          </div>
        </div>
      )}

      {myProcedures.length === 0 && (
        <EmptyState icon="💆" title="Nenhum procedimento cadastrado"
          text="Cadastre seus procedimentos para que os clientes possam agendar."
          action={<Button variant="primary" onClick={openNew}>+ Cadastrar Primeiro Procedimento</Button>} />
      )}

      {/* Modal */}
      <Modal open={modal} onClose={() => setModal(false)} size="lg"
        title={editProc ? 'Editar Procedimento' : 'Cadastrar Procedimento'}
        footer={<>
          <Button variant="secondary" onClick={() => setModal(false)}>Cancelar</Button>
          <Button variant="primary" loading={loading} onClick={handleSave}>
            {editProc ? '✓ Salvar' : isAutonomous ? '✓ Publicar' : '📤 Enviar para Aprovação'}
          </Button>
        </>}>
        <div className="form-row">
          <FormGroup label="Nome do Procedimento" required>
            <Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Ex: Limpeza de Pele Profunda" />
          </FormGroup>
          <FormGroup label="Categoria" required>
            <Select value={form.category} onChange={e=>setForm({...form,category:e.target.value})}>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </Select>
          </FormGroup>
        </div>
        <div className="form-row">
          <FormGroup label="Duração (minutos)" required>
            <Input type="number" min="15" step="15" value={form.duration} onChange={e=>setForm({...form,duration:e.target.value})} placeholder="60" />
          </FormGroup>
          <FormGroup label="Valor (R$)" required>
            <Input type="number" min="0" step="0.01" value={form.price} onChange={e=>setForm({...form,price:e.target.value})} placeholder="150,00" />
          </FormGroup>
        </div>
        <FormGroup label="Emoji / Ícone">
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {EMOJIS.map(e => (
              <div key={e} onClick={() => setForm({...form,emoji:e})}
                style={{ width:38, height:38, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, borderRadius:8, cursor:'pointer', border:`2px solid ${form.emoji===e?'var(--accent)':'var(--border)'}`, background:form.emoji===e?'#fce7eb':'var(--surface)' }}>
                {e}
              </div>
            ))}
          </div>
        </FormGroup>
        <FormGroup label="Descrição">
          <textarea className="form-input" rows={3} value={form.description} onChange={e=>setForm({...form,description:e.target.value})} placeholder="Descreva o procedimento, benefícios, indicações..." />
        </FormGroup>
        {!editProc && (
          <div style={{ background:'#eff6ff', border:'1px solid #93c5fd', borderRadius:'var(--radius)', padding:'12px 14px', fontSize:13, color:'#1e40af', marginTop:8 }}>
            {isAutonomous
              ? 'ℹ️ Como autônomo, este procedimento será publicado automaticamente e ficará disponível para agendamento.'
              : 'ℹ️ Este procedimento será enviado para aprovação do administrador antes de aparecer disponível para agendamento.'}
          </div>
        )}
      </Modal>
    </div>
  );
}
